import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Camera, Globe, Users, ArrowRight, Sparkles, Heart, Menu, X, Plus, User, Crown, Shield, LogOut, Trophy, Palette } from 'lucide-react';
import { MapFilterProvider } from '../contexts/MapFilterContext';
import { InteractiveMap } from './InteractiveMap';
import { FeaturedStories } from './FeaturedStories';
import { Footer } from './Footer';
import { useAuth, LoginForm, UserProfile } from './UserAuth';
import { ClubShowcase } from './ClubShowcase';
import api from '../services/api';

// TravelRock hero images - whimsical painted rocks
const TRAVELROCK_IMAGES = [
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/041d772e-3c9a-4d09-90a9-049becfc36a9/images/e9a1be339cabc466965efff30b294afa6a6ac96d89acd9471c411b38d25f79c2.png',
    title: 'Hello Friend Rock',
    caption: 'Spreading joy one rock at a time'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/041d772e-3c9a-4d09-90a9-049becfc36a9/images/e74ab84553364f9c537cfa5e6a4574be1e8eba67364ab0cdaa08109cc141255c.png',
    title: 'Beach Sunset Rock',
    caption: 'Adventures by the sea'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/041d772e-3c9a-4d09-90a9-049becfc36a9/images/af066edcfc859ed58c31d1891bb3e5e3e62fbf6112b6b216cc5a42a1cd30d2a2.png',
    title: 'Ladybug Castle Rock',
    caption: 'Exploring the world together'
  }
];

// TravelRock Header with Navigation - Whimsical theme
const TravelRockHeader = ({ clubName, tagline }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const { user, logout } = useAuth();
  const [userPoints, setUserPoints] = useState(0);

  useEffect(() => {
    if (user?.email) {
      loadUserPoints();
    }
  }, [user]);

  const loadUserPoints = async () => {
    try {
      const data = await api.getUserPoints(user.email);
      setUserPoints(data.totalPoints || 0);
    } catch (error) {
      console.error('Error loading points:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 shadow-lg border-b border-purple-500" style={{ zIndex: 1000 }}>
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - TravelRock.club branding */}
            <button
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none bg-transparent border-0"
              onClick={() => navigate('/club/travelrock')}
              title="TravelRock.Club Home"
              type="button"
              data-testid="travelrock-logo"
            >
              <span className="text-2xl sm:text-3xl">🪨</span>
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold text-white leading-tight drop-shadow-md">{clubName}</span>
                <span className="text-[8px] sm:text-[10px] text-purple-100 -mt-0.5">{tagline}</span>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('map')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Rock Map
              </button>
              <button 
                onClick={() => scrollToSection('stories')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Rock Stories
              </button>
              <button 
                onClick={() => navigate('/store?theme=travelrock')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Store
              </button>
              <button 
                onClick={() => navigate('/subscription')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                💎 Subscription
              </button>
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <Button
                onClick={() => navigate('/create-item')}
                className="bg-white hover:bg-gray-100 text-purple-600 font-semibold shadow-md"
                data-testid="register-rock-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Register Rock
              </Button>
              
              {user ? (
                <div className="flex items-center gap-2">
                  {user.role === 'admin' ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowUserProfile(true)}
                      className="border-2 border-white/50 bg-white/20 text-white hover:bg-white/30"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      {user.name}
                      <Badge className="ml-2 bg-purple-700 text-white text-xs">Admin</Badge>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setShowUserProfile(true)}
                      className="border-2 border-white/50 bg-white/20 text-white hover:bg-white/30"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {user.name}
                    </Button>
                  )}
                  
                  <div 
                    onClick={() => navigate('/leaderboard')}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full border border-white/30 cursor-pointer hover:bg-white/30 transition-all"
                    title="View Leaderboard"
                  >
                    <Trophy className="w-4 h-4 text-yellow-300" />
                    <span className="font-bold text-white">{userPoints.toLocaleString()}</span>
                    <span className="text-xs text-white/80">pts</span>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      logout();
                      navigate('/club/travelrock');
                    }}
                    className="border-2 border-red-300 bg-red-500/20 text-white hover:bg-red-500/40"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowLoginForm(true)}
                  className="border-2 border-white/50 bg-white/20 text-white hover:bg-white/30"
                  data-testid="login-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Login
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-3 -mr-3 text-white hover:text-white/80 transition-colors"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          <div 
            className="md:hidden fixed inset-x-0 top-14 bottom-0 bg-black/30" 
            style={{ zIndex: 1001 }}
            onClick={() => setIsMenuOpen(false)}
          />
          <div 
            className="md:hidden fixed inset-x-0 top-14 bottom-0 bg-gradient-to-b from-purple-50 to-pink-50 shadow-lg border-t border-purple-300 overflow-y-auto"
            style={{ zIndex: 1002 }}
          >
            <nav className="flex flex-col space-y-2 p-4 pb-20">
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className="text-left text-purple-700 hover:text-purple-900 hover:bg-purple-100 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('map')} 
                className="text-left text-purple-700 hover:text-purple-900 hover:bg-purple-100 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                Rock Map
              </button>
              <button 
                onClick={() => scrollToSection('stories')} 
                className="text-left text-purple-700 hover:text-purple-900 hover:bg-purple-100 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                Rock Stories
              </button>
              <button 
                onClick={() => {
                  navigate('/store?theme=travelrock');
                  setIsMenuOpen(false);
                }} 
                className="text-left text-purple-700 hover:text-purple-900 hover:bg-purple-100 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                Store
              </button>
              <button 
                onClick={() => {
                  navigate('/subscription');
                  setIsMenuOpen(false);
                }} 
                className="text-left text-purple-700 hover:text-purple-900 hover:bg-purple-100 transition-colors font-medium py-3 px-2 rounded-lg"
              >
                💎 Subscription
              </button>
              
              <div className="flex flex-col space-y-3 pt-4 mt-2 border-t border-purple-300">
                <Button 
                  className="h-12 text-base bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold"
                  onClick={() => {
                    navigate('/create-item');
                    setIsMenuOpen(false);
                  }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Register Your Rock
                </Button>

                {user ? (
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowUserProfile(true);
                        setIsMenuOpen(false);
                      }}
                      className="w-full justify-start h-12 text-base border-2 border-purple-400 text-purple-700 hover:bg-purple-100"
                    >
                      {user.role === 'admin' ? <Crown className="w-5 h-5 mr-2" /> : <User className="w-5 h-5 mr-2" />}
                      {user.name}
                      {user.role === 'admin' && <Badge className="ml-2 bg-purple-700 text-white text-xs">Admin</Badge>}
                    </Button>
                    
                    {user.role === 'admin' && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/admin/dashboard');
                          setIsMenuOpen(false);
                        }}
                        className="w-full justify-start h-12 text-base border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                      >
                        <Shield className="w-5 h-5 mr-2" />
                        Admin Dashboard
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        logout();
                        navigate('/club/travelrock');
                        setIsMenuOpen(false);
                      }}
                      className="w-full justify-start h-12 text-base border-2 border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-5 h-5 mr-2" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowLoginForm(true);
                      setIsMenuOpen(false);
                    }}
                    className="w-full justify-start h-12 text-base border-2 border-purple-400 text-purple-700 hover:bg-purple-100"
                  >
                    <User className="w-5 h-5 mr-2" />
                    Login / Sign Up
                  </Button>
                )}
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Auth Modals */}
      {showLoginForm && (
        <div style={{ zIndex: 1100, position: 'relative' }}>
          <LoginForm onClose={() => setShowLoginForm(false)} />
        </div>
      )}
      
      {showUserProfile && user && (
        <div style={{ zIndex: 1100, position: 'relative' }}>
          <UserProfile user={user} onClose={() => setShowUserProfile(false)} />
        </div>
      )}
    </>
  );
};

// TravelRock Hero Component - Whimsical and Fun
const TravelRockHero = ({ images, clubName, tagline }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-900 via-pink-800 to-indigo-900">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        {images.map((image, index) => (
          <div
            key={image.url}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-black/50"></div>
          </div>
        ))}
      </div>

      {/* Decorative floating elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-20 h-20 bg-pink-400/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-32 right-20 w-32 h-32 bg-purple-400/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-yellow-400/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
            <span className="text-2xl">🎨</span>
            <span className="text-sm text-white font-medium" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
              The Painted Rock Tracking Community
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.4)' }}>
            {clubName}
          </h1>
          
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }}>
            <span className="bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300 bg-clip-text text-transparent">
              Every painted rock has a journey and a story to tell
            </span>
          </p>

          {/* Subheadline */}
          <p className="text-base sm:text-lg text-pink-100 mb-8 max-w-2xl mx-auto leading-relaxed" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.3)' }}>
            Track your traveling painted rocks, see where they've been discovered, and spread joy around the world. 
            Hide a rock, scan to track, and watch the magic happen! 🪨✨
          </p>

          {/* Stats Pills */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-pink-300 font-bold">🌎</span>
              <span className="text-white ml-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Rocks Traveling Worldwide</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-purple-300 font-bold">📍</span>
              <span className="text-white ml-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Track Your Rock's Journey</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-indigo-300 font-bold">🎨</span>
              <span className="text-white ml-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Share Your Discoveries</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/create-item')}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-pink-500/50 hover:shadow-pink-500/70 transition-all"
              data-testid="hero-register-rock-btn"
            >
              <Palette className="w-5 h-5 mr-2" />
              Register Your Rock
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                const mapSection = document.getElementById('map');
                mapSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-2 border-white/50 text-white hover:bg-white/10 px-8 py-6 text-lg backdrop-blur-sm"
              data-testid="explore-map-btn"
            >
              <Globe className="w-5 h-5 mr-2" />
              Explore Rock Map
            </Button>
          </div>
        </div>
      </div>

      {/* Animated rock emojis floating */}
      <div className="absolute bottom-10 left-10 animate-bounce text-4xl opacity-50">🪨</div>
      <div className="absolute top-20 right-20 animate-pulse text-3xl opacity-40">🎨</div>
      <div className="absolute bottom-20 right-10 animate-bounce text-2xl opacity-30" style={{animationDelay: '0.5s'}}>💜</div>
      <div className="absolute top-1/3 left-5 animate-pulse text-2xl opacity-30" style={{animationDelay: '1s'}}>✨</div>
    </section>
  );
};

// TravelRock CTA Component
const TravelRockCTA = () => {
  const navigate = useNavigate();
  
  return (
    <section className="py-16 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          Join the Painted Rock Movement! 🎨🪨
        </h2>
        <p className="text-lg text-pink-100 mb-8 max-w-2xl mx-auto">
          Get your trackable QR stickers and be part of the worldwide rock-hiding adventure. 
          Every rock you hide brings a smile to someone's day!
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            onClick={() => navigate('/store?theme=travelrock')}
            className="bg-white hover:bg-gray-100 text-purple-600 px-8 py-6 text-lg font-bold shadow-lg"
            data-testid="get-qr-stickers-btn"
          >
            Get QR Stickers
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => window.open('https://iwashere.me', '_blank')}
            className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-bold"
          >
            Learn More at iWasHere.me
          </Button>
        </div>
      </div>
    </section>
  );
};

// Main TravelRock Club Page Component
export const TravelRockClubPage = () => {
  const [searchParams] = useSearchParams();
  // Default to 'travelrock' for the private tag filter
  const searchTag = searchParams.get('search') || 'travelrock';
  
  // Club configuration
  const clubConfig = {
    name: 'TravelRock.Club',
    tagline: 'Powered by iWasHere.me',
    filterTag: searchTag,
    images: TRAVELROCK_IMAGES
  };
  
  // TravelRock theme - purple/pink colors
  const travelRockTheme = {
    primary: 'purple',
    secondary: 'pink',
    accent: 'purple',
    gradientStyle: 'linear-gradient(to right, #ec4899, #8b5cf6)', // pink-500 to purple-500
    primaryColor: '#8b5cf6', // purple-500
    secondaryColor: '#ec4899', // pink-500
    iconColor: '#8b5cf6'
  };

  return (
    <MapFilterProvider initialSearchTerm={clubConfig.filterTag} isPrivateTagFilter={true} theme={travelRockTheme}>
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50" data-testid="travelrock-club-page">
        {/* TravelRock header with full navigation */}
        <TravelRockHeader 
          clubName={clubConfig.name} 
          tagline={clubConfig.tagline} 
        />
        
        {/* Spacer for fixed header */}
        <div className="h-14 sm:h-16"></div>
        
        <TravelRockHero 
          images={clubConfig.images}
          clubName={clubConfig.name}
          tagline={clubConfig.tagline}
        />
        
        {/* Transition */}
        <div className="h-16 bg-gradient-to-b from-indigo-900 to-purple-50"></div>
        
        {/* How It Works Section */}
        <div id="how-it-works" className="bg-gradient-to-b from-purple-50 to-pink-50 py-12">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-purple-900 mb-2">
                🎨 How TravelRock Works
              </h2>
              <p className="text-purple-700 max-w-2xl mx-auto">
                Join the worldwide painted rock community in 3 simple steps
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-md border border-purple-200 text-center hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">🏷️</div>
                <h3 className="font-bold text-lg text-purple-900 mb-2">1. Get Your QR Stickers</h3>
                <p className="text-purple-700 text-sm">
                  <span className="font-semibold">Print FREE</span> QR stickers at home or <span className="font-semibold">order weatherproof QR stickers</span> from our store to attach to your painted rocks
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-purple-200 text-center hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">🪨</div>
                <h3 className="font-bold text-lg text-purple-900 mb-2">2. Hide Your Rock</h3>
                <p className="text-purple-700 text-sm">Paint a rock, attach the QR sticker, and hide it somewhere fun for others to discover!</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-purple-200 text-center hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">🗺️</div>
                <h3 className="font-bold text-lg text-purple-900 mb-2">3. Track & Share</h3>
                <p className="text-purple-700 text-sm">Watch your rock travel the world as finders scan the QR code and share their discovery photos!</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Map Section */}
        <div id="map" className="bg-purple-100/50">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-purple-900 mb-2">
                🗺️ TravelRock World Map
              </h2>
              <p className="text-purple-700">
                See where painted rocks are traveling around the world
              </p>
            </div>
            <InteractiveMap />
          </div>
        </div>
        
        {/* Stories Section */}
        <div id="stories" className="bg-gradient-to-b from-purple-50 to-pink-50">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-purple-900 mb-2">
                📖 Rock Adventures
              </h2>
              <p className="text-purple-700">
                Stories and discoveries from the TravelRock community
              </p>
            </div>
            <FeaturedStories />
          </div>
        </div>
        
        {/* CTA Section */}
        <TravelRockCTA />
        
        {/* Footer */}
        <ClubShowcase excludeClubId="travelrock" title="Explore More Communities" />
        <Footer />
      </div>
    </MapFilterProvider>
  );
};

export default TravelRockClubPage;
